import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps } from 'antd'
import type { PickerProps } from 'antd/es/date-picker/generatePicker'
import cl from 'classnames'
import type { Moment } from 'moment'

import DatePicker from 'Shared/atoms/DatePicker'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'

import styles from './input.module.scss'

export type FormDatePickerProps<FieldsType extends FieldValues> = Omit<
	PickerProps<Moment>,
	'picker'
> &
	FormItemProps & {
		wrapperClassName?: string
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		validate?: boolean
		isReadOnly?: boolean
		picker?: 'date' | 'year'
		/** Disable dates strictly before this value (use for End Date to block dates before Begin Date) */
		minDate?: Moment | null
		/** Disable dates strictly after this value (use for Begin Date to block dates after End Date) */
		maxDate?: Moment | null
	}

function FormDatePicker<FieldsType extends FieldValues>({
	label,
	wrapperClassName,
	control,
	fieldName,
	validate,
	minDate,
	maxDate,
	...props
}: FormDatePickerProps<FieldsType>) {
	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
				const handleChange = (value: Moment | null, dateString: string) => {
					onChange(value, dateString)
					props.onChange?.(value, dateString)
				}

				const computedDisabledDate = (current: Moment): boolean => {
					if (minDate && current.isBefore(minDate, 'day')) return true
					if (maxDate && current.isAfter(maxDate, 'day')) return true
					return props.disabledDate?.(current) ?? false
				}

				return (
					<FormItem
						className={cl(styles.inputWrapper, wrapperClassName)}
						label={label}
						name={name}
						error={hasError(error)}
						extra={props.extra}
						validate={validate}
					>
						<DatePicker {...props} value={value} ref={ref} onChange={handleChange} disabledDate={computedDisabledDate} />
					</FormItem>
				)
			}}
		/>
	)
}

export default FormDatePicker
